import * as React from "react";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@emotion/react";
import { useLayout } from "../../../middlewares/LayoutContext";
import { usePageTitle } from "../../../middlewares/PageTitleContext";
import drawerDim from "../../../theme/drawerDimensions";
import {
  Backdrop,
  Badge,
  Button,
  Box,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  Tooltip,
  alpha,
} from "@mui/material";
import ProfileMenu from "../../Profile/Menu";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  receive,
  reset,
  receiveAll,
  readOne,
  readAll,
} from "../../../Redux/notificationSlice";
import {
  profileGetSignedURLRoute,
  sendNotificationRoute,
  markAsReadNotificationRoute,
  markAllAsReadNotificationRoute,
  messagesUnreadCountRoute,
} from "../../../routes/apiRoutes";
import { useSpring, animated } from "react-spring";
import { useNavigate } from "react-router";
import {
  DoneAllRounded,
  ElectricBolt,
  NotificationsNoneRounded,
} from "@mui/icons-material";
import OpenNotificationHeader from "./OpenNotificationHeader";
import axiosInstance from "../../../axios/axiosInstance";
import { t } from "i18next";
import { useMediaQuery } from "@mui/material";
import { useCurrentPage } from "../../../hooks/useCurrentPage";
import { canFetchMessagesUnreadCount } from "../../../utils/managementPermissions";

function AppTopBar() {
  const { handleDrawerToggle } = useLayout();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state) => state.authenticationValues.currentUser
  );
  const avatar = useSelector((state) => state.authenticationValues.avatar);
  const messages = useSelector((state) => state.notificationValues.messages);
  const [pageInfo, setPageInfo] = React.useState(1);
  const [msgcount, setMsgCount] = React.useState(0);
  const [scrolling, setScrolling] = React.useState(false);
  const count = useSelector((state) => state.notificationValues.unreadCount);
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();
  const canFetchUnreadMessages = canFetchMessagesUnreadCount(currentUser);

  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const isCollapsed = useSelector((state) => state.collapseValues.sidebarCollapsed);
  
  // Media query for mobile detection
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get page title from context
  const { pageTitle, pageSubtitle } = usePageTitle();

  // ... (keep all the existing notification logic)

  //Bell animation//////////////////
  const [, setIsAnimating] = React.useState(false);
  const animation = useSpring({
    from: { transform: "rotate(0deg)" },
    to: async (next) => {
      await next({ transform: "rotate(-20deg)" });
      await next({ transform: "rotate(20deg)" });
      await next({ transform: "rotate(0deg)" });
    },
    config: { duration: 500 },
    onRest: () => setIsAnimating(false),
  });

  React.useEffect(() => {
    if (count) setIsAnimating(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  ///////////////////////////////////

  //Get all messages from backend on first render
  React.useEffect(() => {
    receiveMessages("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (pageInfo > 1 && messages.length > 0 && !scrolling) {
      receiveMessagesScroll(listType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo]);

  async function receiveMessages(selected) {
    setIsLoading(true);
    try {
      const requests = [
        axiosInstance.get(
          `${sendNotificationRoute}?limit=10&&page=1&&id=${currentUser.id}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        ),
      ];
      if (canFetchUnreadMessages) {
        requests.push(
          axiosInstance.get(messagesUnreadCountRoute, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          })
        );
      }
      const [notifRes, unreadRes] = await Promise.all(requests);
      const data = notifRes.data;
      if (data.statusCode === 200 || data.statusCode === 201 || data.status === "success") {
        const oldMessages = [];
        data.data.notifications.forEach((notification) => {
          const notificationKey = `${notification?.title}-${notification?.body}`;
          oldMessages.push({
            body: notification?.body,
            title: notification?.title,
            notificationId: notification.id,
            isRead: notification.is_read,
            date: notification.createdAt,
            msgType: notification?.type,
            patientId: notification?.patient_id || notification?.task?.patient_id || notification?.case?.patient_id || null,
            key: notificationKey,
            ...(notification?.type &&
              notification?.id && {
                type: notification?.type,
                id: notification?.id,
              }),
          });
        });
        // Use total count from server for pagination window, not unread count
        const totalCount =
          data?.data?.totalCount ??
          data?.data?.pagination?.total ??
          data?.response?.totalCount ??
          0;
        setMsgCount(totalCount);
        dispatch(
          receiveAll({
            messages: [...oldMessages],
            unreadCount: data?.data?.unreadCount ?? 0,
            messagesUnreadCount: unreadRes?.data?.data?.unreadCount ?? 0,
          })
        );
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  }
  const handleListScroll = debounce((event) => {
    const bottomMargin =
      event.target.clientHeight -
      event.target.scrollHeight +
      event.target.scrollTop;
    if (bottomMargin > -20 && messages.length < msgcount && !scrolling) {
      const nextPage = Math.floor(messages.length / 10) + 1;
      setPageInfo(nextPage);
    }
  }, 300);

  async function receiveMessagesScroll(selected) {
    setScrolling(true);
    try {
      const { data } = await axiosInstance.get(
        `${sendNotificationRoute}?limit=10&&page=${pageInfo}&&id=${currentUser.id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (data.statusCode === 200 || data.statusCode === 201 || data.status === "success") {
        const oldMessages = [...messages];
        data.data.notifications?.forEach((notification) => {
          const notificationKey = `${notification?.title}-${notification?.body}`;

          oldMessages.push({
            body: notification?.notificationData?.body || notification?.body,
            title: notification?.notificationData?.title || notification?.title,
            notificationId: notification.id,
            patientId: notification?.patient_id || notification?.task?.patient_id || notification?.case?.patient_id || notification?.notificationData?.patientId || null,
            isRead: notification.is_read || notification.isRead,
            date: notification.createdAt,
            msgType: notification?.notificationData?.msgType || notification.type,
            key: notificationKey,
            ...(notification?.notificationData?.type && notification?.notificationData?.id && {
              type: notification?.notificationData?.type,
              id: notification?.notificationData?.id,
            }),
          });
        });
        dispatch(receiveAll({ messages: [...oldMessages] }));
      }
    } catch {
    } finally {
      setScrolling(false);
    }
  }

  //show messages and read all messages on bell icon click
  const handleNotifications = (event) => {
    setAnchorNotify(event.currentTarget);
    setOpenNotify(true);
    // Refresh unread count/messages from server on open to avoid stale Redux-only state
    receiveMessages("all");
  };

  const readOneMessage = async (id, userId) => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.put(
        `${markAsReadNotificationRoute}`,
        {
          notificationId: id,
          userId: userId,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (data.statusCode === 200 || data.statusCode === 201 || data.status === "success") {
        setIsLoading(false);
        dispatch(readOne({ notificationId: id }));
      }
    } catch {
      setIsLoading(false);
    }
  };

  const readAllMessages = async () => {
    try {
      if (!currentUser?.id || !count) return;
      setIsLoading(true);
      const { data } = await axiosInstance.put(
        `${markAllAsReadNotificationRoute}`,
        { userId: currentUser.id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (data?.status === "success" || data?.statusCode === 200 || data?.statusCode === 201) {
        dispatch(readAll());
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const getLatestPreviewLink = async (url) => {
    try {
      setScrolling(true);
      const { data } = await axiosInstance.post(
        profileGetSignedURLRoute,
        { url: url },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (data.statusCode === 200 || data.statusCode === 201) {
        setScrolling(false);
        return data?.response?.url;
      } else return null;
    } catch {
      setScrolling(false);
      return null;
    }
  };

  const handleMessageClick = async (id, type, messageData, event) => {
    
      if (!event.openMessage) {
        if (!messageData.isRead) {
          readOneMessage(messageData.notificationId,currentUser?.id);
        }
        handleCloseNotify();
        
        // Navigate based on notification type and available data
        if (messageData.patientId) {
          // If we have a patient ID, always navigate to patient details
          navigate(`/patients/${messageData.patientId}`);
        } else if (type === "case") {
          navigate(`/case-days`);
        } else if (type === "task_due") {
          navigate(`/tasks`);
        } else {
          // Default fallback - open the notification detail
          setOpenNotification(true);
          setOpenData(messageData);
        }
      }
      event.openMessage = false;
      
  };

  const handleMessageClickOpen = (messageData, event) => {
    event.openMessage = true;
    if (!messageData.isRead) {
      readOneMessage(messageData.notificationId, currentUser?.id);
    }
  };

  function timeAgo(date) {
    try {
      const now = new Date();
      const diff = now - new Date(date);

      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (seconds < 60) {
        return t("justNow");
      } else if (minutes < 60) {
        return t("minutesAgo", { minutes });
      } else if (hours < 24) {
        return t("hoursAgo", { hours });
      } else {
        return t("daysAgo", { days });
      }
    } catch {
      return null;
    }
  }

  const [listType, setListType] = React.useState("all");
  const [openNotification, setOpenNotification] = React.useState(false);
  const [openData, setOpenData] = React.useState({});
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorNotify, setAnchorNotify] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [openNotify, setOpenNotify] = React.useState(false);

  // Get current page name
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  React.useEffect(() => {
    if (!open) handleClose();
  }, [open]);
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const handleCloseNotify = () => {
    setAnchorNotify(null);
    setOpenNotify(false);
  };
  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { 
          xs: '100%', // Mobile: full width
          sm: '100%', // Tablet: full width  
          md: `calc(100% - ${isCollapsed ? drawerDim.collapsedWidth : drawerDim.width}px)` // Desktop: account for sidebar
        },
        ml: { 
          xs: 0, // Mobile: no margin
          sm: `${drawerDim.collapsedWidth}px`, // Tablet: no margin
          md:  `${isCollapsed ? drawerDim.collapsedWidth : drawerDim.width}px` // Desktop: margin for sidebar
        },
        height: drawerDim.height,
        borderBottom: `1px solid ${theme.palette.componentBackgrounds.border}`,
        background: theme.palette.whiteblack.main,
      }}
    >
      <OpenNotificationHeader
        open={openNotification}
        setOpen={setOpenNotification}
        data={openData}
      />
      <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
        {/* Mobile hamburger button - only show on mobile */}
        {isMobile && (
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mx: 1,
              color: theme.palette.primary.main,
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
         {/* Current Page Name - show on all devices */}
         <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: '45%',
            flex: 1,
            px: { xs: 0, sm: 2 },
          }}
        >
          <Typography
          variant="h5"
          sx={{
            color: theme.palette.text.main,
            fontWeight: 500,
            ml: { 
              xs: 0,
              sm: `${drawerDim.collapsedWidth}px`,
              md: 0
            },
            whiteSpace: 'nowrap',
          }}
        >
          {pageTitle}
        </Typography>
        </Box>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            justifyContent: "right",
            width: "100%",
            mx: 0.5,
            flexDirection: isRtl ? "row-reverse" : "row",
          }}
        >
          <Tooltip title="View Notifications">
            <Badge
              badgeContent={count}
              overlap="circular"
              color="primary"
              sx={{ mr: 1 }}
            >
              <IconButton
                data-tour="topbar-notifications"
                sx={{
                  width: 40,
                  height: 40,
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                }}
                onClick={handleNotifications}
              >
                {count > 0 && (
                  <animated.div style={{ ...animation, fontSize: "0.8rem" }}>
                    <NotificationsActiveIcon color="primary" />
                  </animated.div>
                )}
                {count === 0 && <NotificationsActiveIcon color="primary" />}
              </IconButton>
            </Badge>
          </Tooltip>
          <img
            src={avatar ? avatar : "avatar.svg"}
            alt="avatar"
            width={"40px"}
            height={"40px"}
            style={{ borderRadius: "50%" }}
          ></img>
          
         {!isMobile && (
           <Box sx={{ display: "flex", flexDirection: "column" }}>
           <Typography
             sx={{
               color: theme.palette.whiteblack.contrastText,
               pl: 1,
               fontWeight: 700,
               fontSize: "1rem",
             }}
             noWrap
             component="div"
           >
             {!isRtl
               ? `${t("hi")} ${currentUser.firstName}`
               : `${currentUser.firstName} ${t("hi")}`}
           </Typography>
         </Box>
         )}
          <IconButton
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <KeyboardArrowDownIcon color="primary"></KeyboardArrowDownIcon>
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ vertical: -20, horizontal: 0 }}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <ProfileMenu setOpen={setOpen} />
          </Menu>
          <Menu
            id="notification-menu"
            anchorEl={anchorNotify}
            slotProps={{
              paper: { sx: { width: 500, height: 600, overflow: "hidden" } },
            }}
            open={openNotify}
            onClose={handleCloseNotify}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            MenuListProps={{
              "aria-labelledby": "basic-notify-button",
            }}
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "550px",
                }}
              >
                <CircularProgress color="primary" />
              </div>
            ) : (
              <div dir={theme.direction} position={"relative"} width={"100%"} padding={0}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.componentBackgrounds.border}`,
                    backgroundColor: theme.palette.whiteblack.main,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                    {t("notifications")}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={readAllMessages}
                    disabled={!count || count === 0}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {t("markallread")}
                  </Button>
                </Box>
                {messages.length > 0 ? (
                  <ul
                    onScroll={handleListScroll}
                    style={{
                      padding: 0,
                      overflow: "auto",
                      height: 510,
                    }}
                  >
                    {messages.map((message, index) => {
                      return (
                        <div
                          onClick={(e) =>
                          {
                           
                              // handleMessageClickOpen(message, e)
                              handleMessageClick(
                                message.id,
                                message.msgType,
                                message,
                                e
                              )
                          } 
                          }
                          style={{
                            cursor: "pointer",
                            backgroundColor: !message.isRead
                              ? alpha(theme.palette.primary.main, 0.05)
                              : theme.palette.whiteblack.main,
                          }}
                          key={index}
                        >
                          <Box sx={{ py: 1, px: 1.5 }}>
                            <Box display={"flex"} gap={2} alignItems={"center"}>
                              {message.msgType === "Info" ? (
                                <ElectricBolt
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: "#ffbf00",
                                  }}
                                />
                              ) : (
                                <NotificationsNoneRounded
                                  sx={{
                                    fontSize: "1.1rem",
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  fontSize: "0.9rem",
                                  fontStyle: "italic",
                                  color: !message.isRead
                                    ? theme.palette.cancelText.inactiveFixed
                                    : theme.palette.cancelText.inactive,
                                }}
                              >
                                Notification
                              </Typography>
                              {!message.isRead && (
                                <Tooltip title={t("markasread")}>
                                  <IconButton
                                    onClick={(e) =>
                                      handleMessageClickOpen(message, e)
                                    }
                                    sx={{ p: 0, m: 0 }}
                                  >
                                    <DoneAllRounded
                                      sx={{
                                        fontSize: "1rem",
                                        color:
                                          message.msgType === "Info"
                                            ? "#ffbf00"
                                            : theme.palette.primary.main,
                                      }}
                                    ></DoneAllRounded>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <ListItemText
                              sx={{ ml: 4 }}
                              primary={message.title}
                              primaryTypographyProps={{
                                sx: {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: "0.9rem",
                                  fontWeight: 500,
                                },
                              }}
                              secondary={
                                <Typography
                                  sx={{
                                    fontSize: "0.875rem",
                                    display: "-webkit-box",
                                    WebkitLineClamp: "2",
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  variant="body2"
                                >
                                  {message.body}
                                </Typography>
                              }
                            ></ListItemText>
                            {message.date && (
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  fontStyle: "italic",
                                  color: theme.palette.cancelText.inactive,
                                  ml: 4,
                                }}
                              >
                                {timeAgo(message.date)
                                  ? timeAgo(message.date)
                                  : ""}
                              </Typography>
                            )}
                          </Box>
                          <Divider style={{ width: "100%" }} />
                        </div>
                      );
                    })}
                    <Backdrop
                      sx={{
                        color: "#fff",
                        position: "absolute",
                        height: "100%",
                        overflow: "hidden",
                        background: "#00000025",
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                      }}
                      open={scrolling}
                    >
                      <CircularProgress />
                    </Backdrop>
                  </ul>
                ) : (
                  <Box
                    sx={{
                      height: "550px",
                      width: "100%",
                      display: "flex",
                      mt: -1,
                      flexDirection: "column",
                      background: "transparent",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <NotificationsNoneRounded
                      sx={{ fontSize: "3rem" }}
                    ></NotificationsNoneRounded>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
                      {t("nonotifications")}
                    </Typography>
                    <Typography>{t("notifyinfo")}</Typography>
                  </Box>
                )}
              </div>
            )}
          </Menu>
        </Toolbar>
      </div>
    </AppBar>
  );
}
export default AppTopBar;